import { useMutation, useQuery } from '@apollo/client';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { v4 } from 'uuid';

import { TsrctCommentParentVariant } from '@/generated/gql/graphql';

import {
  ADD_COMMENT_MUTATION,
  COMMENTS_QUERY,
  DELETE_COMMENT_MUTATION,
  EDIT_COMMENT_MUTATION,
} from './CommentsQuery.gql';

interface UseCommentsProps {
  entityId: string;
  entityVariant: TsrctCommentParentVariant;
  isOpen: boolean;
}

export const useComments = ({ entityId, entityVariant, isOpen }: UseCommentsProps) => {
  const { onError } = useCustomSnackbar();

  const { data, loading } = useQuery(COMMENTS_QUERY, {
    variables: {
      entityId,
      entityVariant,
    },
    skip: !isOpen,
  });

  const [addCommentMutation, { loading: addingComment }] = useMutation(ADD_COMMENT_MUTATION, {
    refetchQueries: [{ query: COMMENTS_QUERY, variables: { entityId, entityVariant } }],
    onError,
  });

  const [editCommentMutation, { loading: editingComment }] = useMutation(EDIT_COMMENT_MUTATION, {
    refetchQueries: [{ query: COMMENTS_QUERY, variables: { entityId, entityVariant } }],
    onError,
  });

  const [deleteCommentMutation, { loading: deletingComment }] = useMutation(DELETE_COMMENT_MUTATION, {
    refetchQueries: [{ query: COMMENTS_QUERY, variables: { entityId, entityVariant } }],
    onError,
  });

  const addComment = (message: string) => {
    addCommentMutation({
      variables: {
        id: v4(),
        message,
        parentId: entityId,
        parentVariant: entityVariant,
      },
    });
  };

  const editComment = (id: string, message: string) => {
    editCommentMutation({
      variables: {
        id,
        message,
        parentId: entityId,
        parentVariant: entityVariant,
      },
    });
  };

  const deleteComment = (id: string) => {
    deleteCommentMutation({
      variables: { id },
    });
  };

  return {
    addComment,
    editComment,
    deleteComment,
    commentsData: data?.tesseract?.comments,
    commentsLoading: loading,
    addingComment,
    editingComment,
    deletingComment,
  };
};
